import { useState } from 'react';
import type { AdminUser } from '@shared/types';
import { ROLE_LABELS, type AdminRole } from '@shared/roles';
import { useAuth } from '../app/auth';
import { api, ApiError, describeError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { formatDateTime } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  Modal,
  Notice,
  PageHeader,
  Select,
  Table,
  type Column,
} from '../components/ui';

const ROLE_OPTIONS = (['super_admin', 'ops', 'support', 'finance', 'viewer'] as const).map(
  (role) => ({ value: role, label: ROLE_LABELS[role] }),
);

export function StaffPage() {
  const { session } = useAuth();
  const [version, setVersion] = useState(0);
  const [creating, setCreating] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<AdminRole>('ops');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string | null>(null);

  const staff = useAsync<{ items: AdminUser[] }>(() => api.get('/api/staff'), [version]);

  async function create() {
    setSaving(true);
    setError(null);
    setFieldErrors({});
    try {
      await api.post('/api/staff', {
        email: email.trim(),
        name: name.trim(),
        role,
        password,
      });
      setCreating(false);
      setEmail('');
      setName('');
      setPassword('');
      setRole('ops');
      setNotice('员工账号已创建，首次登录需改密');
      setVersion((value) => value + 1);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setFieldErrors(caught.fieldErrors);
        setError(caught.message);
      } else {
        setError(describeError(caught));
      }
    } finally {
      setSaving(false);
    }
  }

  async function update(user: AdminUser, patch: { role?: AdminRole; status?: 'active' | 'suspended' }) {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await api.patch(`/api/staff/${user.id}`, patch);
      setNotice('员工信息已更新');
      setVersion((value) => value + 1);
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setSaving(false);
    }
  }

  const columns: Column<AdminUser>[] = [
    {
      key: 'email',
      header: '员工',
      render: (row) => (
        <div>
          <p className="text-fg">{row.email}</p>
          <p className="text-xs text-fg-subtle">{row.name}</p>
        </div>
      ),
    },
    {
      key: 'role',
      header: '角色',
      render: (row) => (
        <Select
          value={row.role}
          onChange={(value) => void update(row, { role: value })}
          options={ROLE_OPTIONS}
          disabled={row.id === session?.user.id || saving}
        />
      ),
    },
    {
      key: 'totp',
      header: '两步验证',
      render: (row) =>
        row.totpEnabled ? <Badge tone="ok">已开启</Badge> : <Badge tone="warn">未开启</Badge>,
    },
    {
      key: 'lastLogin',
      header: '最近登录',
      render: (row) => (
        <span className="text-xs text-fg-muted">{formatDateTime(row.lastLoginAt)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '110px',
      render: (row) =>
        row.id === session?.user.id ? (
          <span className="text-xs text-fg-subtle">当前账号</span>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            loading={saving}
            onClick={() => void update(row, { status: 'suspended' })}
          >
            停用
          </Button>
        ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="员工"
        description="后台员工与客户账号完全分开。停用员工会立刻踢出他的全部会话。"
        action={
          <Button variant="primary" onClick={() => setCreating(true)}>
            新建员工
          </Button>
        }
      />

      {notice ? <Notice tone="ok">{notice}</Notice> : null}
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Notice tone="warn">
        被停用的员工不会从列表里消失，重新启用即可；不要共用员工账号。
      </Notice>

      <Card>
        <CardHeader title="员工列表" hint={`${staff.data?.items.length ?? 0} 人`} />
        <Table
          columns={columns}
          rows={staff.data?.items ?? []}
          getKey={(row) => row.id}
          loading={staff.loading}
          empty="还没有员工账号"
        />
      </Card>

      <Modal
        open={creating}
        title="新建员工"
        description="初始口令由你设定，员工首次登录后必须修改。"
        onClose={() => setCreating(false)}
        footer={
          <>
            <Button onClick={() => setCreating(false)}>取消</Button>
            <Button variant="primary" loading={saving} onClick={() => void create()}>
              创建
            </Button>
          </>
        }
      >
        {error ? (
          <div className="mb-3">
            <Notice tone="danger">{error}</Notice>
          </div>
        ) : null}
        <div className="space-y-3">
          <Field label="邮箱" htmlFor="staff-email" error={fieldErrors.email}>
            <Input id="staff-email" type="email" value={email} onChange={setEmail} />
          </Field>
          <Field label="姓名" htmlFor="staff-name" error={fieldErrors.name}>
            <Input id="staff-name" value={name} onChange={setName} maxLength={40} />
          </Field>
          <Field label="角色" htmlFor="staff-role">
            <Select id="staff-role" value={role} onChange={setRole} options={ROLE_OPTIONS} />
          </Field>
          <Field
            label="初始口令"
            htmlFor="staff-password"
            error={fieldErrors.password}
            hint="至少 10 位，包含字母与数字"
          >
            <Input
              id="staff-password"
              type="password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
            />
          </Field>
        </div>
      </Modal>
    </div>
  );
}
